import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { openRazorpay } from "@/lib/razorpay";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Check, CreditCard, Smartphone, Landmark, Layers, Wallet, ShieldCheck, ArrowLeft,
  Calendar, IndianRupee, Sparkles, Building2, QrCode, Lock,
} from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

/**
 * PayForBooking — dedicated payment page for pending-payment bookings created
 * from a bid acceptance or an accepted negotiation. Uses the same Razorpay-style
 * payment UI as the direct booking dialog but on a full page (better on mobile).
 */
export default function PayForBooking() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  useSEO({ title: "Complete payment · SwaanStay", description: "Pay for your confirmed hotel booking." });

  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState("upi");
  const [useWallet, setUseWallet] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/bookings/${bookingId}/payment-intent`)
      .then(({ data }) => setIntent(data))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Could not load payment details");
        navigate("/bookings");
      })
      .finally(() => setLoading(false));
  }, [bookingId, navigate]);

  const breakup = intent?.breakup;
  const walletAvail = intent?.wallet_balance || 0;
  const walletApplicable = useWallet ? Math.min(walletAvail, breakup?.total || 0) : 0;
  const payable = Math.max(0, (breakup?.total || 0) - walletApplicable);

  const walletOnlyConfirm = async () => {
    // No cash portion — just call /pay with wallet_used to finalise.
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/pay`, {
        payment_method: "wallet_only",
        wallet_used: walletApplicable,
      });
      if (data.ok) {
        toast.success("Payment confirmed! Invoice + confirmation sent to your email & WhatsApp.");
        navigate(`/invoice/${bookingId}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not confirm booking");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (payable === 0) return walletOnlyConfirm();
    setBusy(true);
    try {
      // 1. Create Razorpay order server-side (amount authoritative).
      const { data: order } = await api.post(`/payments/razorpay/create-order`, {
        booking_id: bookingId,
        wallet_used: walletApplicable,
      });
      // 2. Open Razorpay Checkout — new order = new dynamic UPI QR every time.
      await openRazorpay({
        keyId: order.key_id,
        orderId: order.order_id,
        amount: order.amount,
        currency: order.currency,
        name: "SwaanStay",
        description: order.notes?.hotel_name || "Hotel booking",
        prefill: order.customer,
        notes: {
          booking_id: order.booking_id,
          hotel_name: order.notes?.hotel_name || "",
        },
        method: paymentMethod, // "upi" → dynamic QR pane loads by default
        onSuccess: async (res) => {
          try {
            const { data: v } = await api.post(`/payments/razorpay/verify`, {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            if (v.ok) {
              toast.success(`Payment received (${v.payment_id}). Invoice on the way!`);
              navigate(`/invoice/${bookingId}`);
            }
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment succeeded but verification failed. Please contact support.");
          } finally {
            setBusy(false);
          }
        },
        onDismiss: (err) => {
          setBusy(false);
          if (err?.description) {
            toast.error(err.description);
          } else {
            toast.info("Payment cancelled. Your booking is held for a short while — you can retry.");
          }
        },
      });
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || e?.message || "Could not start payment");
    }
  };

  if (loading || !intent) {
    return (
      <div className="min-h-[calc(100vh-4rem)] grid place-items-center bg-slate-50 text-slate-500" data-testid="pay-loading">
        Loading…
      </div>
    );
  }

  const alreadyConfirmed = intent.status === "confirmed";

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]" data-testid="pay-page">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(16,185,129,0.4) 0px, transparent 50%), radial-gradient(circle at 80% 70%, rgba(59,130,246,0.4) 0px, transparent 50%)" }} />
        <div className="relative max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-10">
          <button onClick={() => navigate(-1)} className="hw-label text-white/70 hover:text-white flex items-center gap-1" data-testid="pay-back-btn">
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
          <div className="hw-label text-emerald-300 mt-4">
            {intent.source === "bid" && "Bid acceptance"}
            {intent.source === "negotiation" && "Negotiated offer"}
            {intent.source === "direct" && "Direct booking"}
          </div>
          <h1 className="hw-head text-3xl md:text-4xl font-black text-white mt-2">Complete your payment</h1>
          <p className="text-slate-300 text-sm mt-2">
            Your booking is locked at the offer price. Complete payment to receive the invoice on email &amp; WhatsApp.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 pb-24 grid gap-5">
        {/* Booking summary */}
        <div className="hw-card p-5" data-testid="pay-summary">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-md bg-emerald-100 grid place-items-center flex-shrink-0">
              <Building2 className="w-5 h-5 text-emerald-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="hw-label text-blue-700">Confirmed at</div>
              <h2 className="hw-head text-xl md:text-2xl font-black text-slate-900 mt-1 truncate" data-testid="pay-hotel-name">
                {intent.hotel?.name}
              </h2>
              {intent.hotel?.city && <div className="text-sm text-slate-500 mt-0.5">{intent.hotel.city}</div>}
              <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-slate-700">
                <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {intent.checkin} → {intent.checkout}</span>
                <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> {intent.rooms} room{intent.rooms > 1 ? "s" : ""}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Price breakup */}
        <div className="hw-card p-5" data-testid="pay-breakup">
          <div className="hw-label text-slate-500 mb-3">Price breakup</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Room tariff</span><span className="font-semibold text-slate-900" data-testid="pb-base">₹{breakup.base.toLocaleString("en-IN")}</span></div>
            {breakup.discount > 0 && (
              <div className="flex justify-between text-emerald-700"><span>Discount</span><span className="font-semibold">− ₹{breakup.discount.toLocaleString("en-IN")}</span></div>
            )}
            <div className="flex justify-between"><span className="text-slate-600">GST (12%)</span><span className="font-semibold text-slate-900" data-testid="pb-taxes">₹{breakup.taxes.toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Service fee</span><span className="font-semibold text-slate-900" data-testid="pb-service">₹{breakup.service.toLocaleString("en-IN")}</span></div>
            {walletApplicable > 0 && (
              <div className="flex justify-between text-slate-700"><span>Wallet applied</span><span className="font-semibold">− ₹{walletApplicable.toLocaleString("en-IN")}</span></div>
            )}
            <div className="my-2 h-px bg-slate-200" />
            <div className="flex justify-between text-lg pt-1">
              <span className="hw-head font-black text-slate-900">Total payable</span>
              <span className="hw-head font-black text-slate-900 flex items-center" data-testid="pb-total">
                <IndianRupee className="w-4 h-4" />{payable.toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        </div>

        {/* Wallet */}
        {walletAvail > 0 && (
          <div className="hw-card p-5" data-testid="pay-wallet-section">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={useWallet}
                onChange={(e) => setUseWallet(e.target.checked)}
                className="w-4 h-4 accent-blue-700"
                data-testid="pay-wallet-toggle"
              />
              <Wallet className="w-5 h-5 text-emerald-700" />
              <div className="flex-1">
                <div className="font-semibold text-slate-900">Use SwaanStay Wallet</div>
                <div className="text-xs text-slate-500">Available: ₹{walletAvail.toLocaleString("en-IN")}</div>
              </div>
              {useWallet && <span className="text-sm font-bold text-emerald-700">−₹{walletApplicable.toLocaleString("en-IN")}</span>}
            </label>
          </div>
        )}

        {/* Payment method */}
        {payable > 0 && (
          <div className="hw-card overflow-hidden" data-testid="pay-methods">
            <div className="px-5 py-3 flex items-center justify-between border-b border-slate-100">
              <div className="hw-label text-slate-500 flex items-center gap-1.5">
                <CreditCard className="w-3 h-3" /> Payment method
              </div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                <Lock className="w-2.5 h-2.5" /> Secured by Razorpay
              </span>
            </div>
            <div className="grid grid-cols-4 gap-1 bg-slate-50">
              {[
                { k: "upi", label: "UPI", icon: Smartphone },
                { k: "card", label: "Cards", icon: CreditCard },
                { k: "netbanking", label: "Netbanking", icon: Landmark },
                { k: "wallet", label: "Wallets", icon: Layers },
              ].map((m) => (
                <button
                  key={m.k}
                  type="button"
                  onClick={() => setPaymentMethod(m.k)}
                  className={`p-4 text-center transition-colors relative ${
                    paymentMethod === m.k ? "bg-white border-b-2 border-blue-700" : "hover:bg-white/60"
                  }`}
                  data-testid={`pay-method-${m.k}`}
                >
                  <m.icon className={`w-5 h-5 mx-auto ${paymentMethod === m.k ? "text-blue-700" : "text-slate-600"}`} />
                  <div className={`text-xs mt-1 font-bold ${paymentMethod === m.k ? "text-blue-700" : "text-slate-700"}`}>{m.label}</div>
                </button>
              ))}
            </div>

            {paymentMethod === "upi" && (
              <div className="p-5 bg-emerald-50 border-t border-emerald-200" data-testid="upi-pane">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-md bg-white border border-emerald-200 grid place-items-center flex-shrink-0">
                    <QrCode className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div className="flex-1">
                    <div className="hw-label text-blue-700">Pay via UPI QR</div>
                    <div className="text-sm font-bold text-slate-900 mt-1">
                      A fresh QR code is generated for every transaction.
                    </div>
                    <p className="text-xs text-slate-600 mt-1">
                      Tap <span className="font-bold">Pay ₹{payable.toLocaleString("en-IN")}</span> below — the Razorpay window will
                      open with a scannable QR unique to your booking. Any UPI app (GPay, PhonePe, Paytm, BHIM) works.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {paymentMethod !== "upi" && (
              <div className="p-5 bg-slate-50 border-t border-slate-100 text-sm text-slate-700" data-testid={`pane-${paymentMethod}`}>
                Tap <span className="font-bold">Pay ₹{payable.toLocaleString("en-IN")}</span> below to open the secure Razorpay window
                and complete your payment.
              </div>
            )}
          </div>
        )}

        {payable === 0 && walletApplicable > 0 && (
          <div className="hw-card p-5 bg-emerald-50 border-emerald-300 flex items-center gap-3" data-testid="pay-wallet-full">
            <Wallet className="w-6 h-6 text-emerald-700" />
            <div>
              <div className="hw-head font-bold text-emerald-800">Fully paid by wallet</div>
              <div className="text-xs text-emerald-700">No further payment required. Tap Confirm to lock the reservation.</div>
            </div>
          </div>
        )}

        {alreadyConfirmed && (
          <div className="hw-card p-5 bg-blue-50 border-blue-300 text-center" data-testid="pay-already-confirmed">
            This booking is already paid. <a href={`/invoice/${bookingId}`} className="text-blue-700 font-bold underline">View invoice</a>.
          </div>
        )}
      </div>

      {/* Sticky action bar */}
      {!alreadyConfirmed && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-slate-200 bg-white shadow-2xl p-4 z-40" data-testid="pay-action-bar">
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
            <div>
              <div className="hw-label text-slate-500">You pay</div>
              <div className="hw-head text-2xl font-black text-slate-900">
                ₹{payable.toLocaleString("en-IN")}
              </div>
            </div>
            <Button
              onClick={submit}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold py-6 h-auto px-8 text-base"
              data-testid="pay-confirm-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {busy ? "Processing…" : payable === 0 ? "Confirm booking" : `Pay ₹${payable.toLocaleString("en-IN")}`}
              <Check className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
